import React, {memo, useEffect} from 'react';
import {
   Avatar,
   ControlButton,
   TscButtonType,
   Glyphs,
   Label,
   Spinner
} from '@techsmith/tsc-cloud-style-guide';
import {IMemberDetailsWithProfileImageModel} from '../../../model/appModel';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {GroupRoleTypes} from '../../../constants/Constants';
import {useTscTheme} from '../../../context/themeProvider';

export const Wrapper = styled.div`
   &:last-child {
      padding-bottom: 0.75rem;
   }
`;

const RoleLabel = styled(Label)`
   font-style: italic;
   font-weight: 400;
   text-align: right;
   margin-right: 0;
`;

const FlexContainer = styled.div`
   display: flex;
   flex-direction: column;
   flex: 1 1 auto;
   justify-content: center;
   overflow: hidden;
`;

const Text = styled.span`
   text-overflow: ellipsis;
   overflow: hidden;
   white-space: nowrap;
`;

const PrimaryText = styled(Text)`
   font-size: 0.875rem;
   font-weight: 500;
`;

const GridMemberRow = styled.div<{changesDisabled: boolean}>`
   display: grid;
   grid-template-columns: 2rem 3fr 1fr 2rem;
   grid-gap: 0.75rem;
   align-items: center;

   padding: 0.75rem 0.75rem 0 0.75rem;
`;

const RemoveButton = styled(ControlButton)`
   width: 32px;
   height: 32px;
`;

export const MemberSpinner = styled(Spinner)`
   height: 16px;
   width: 16px;
   svg {
      height: 16px;
      width: 16px;
   }
`;

export const TeamMemberListItemBase: React.FC<ITeamMemberListItemProps> = ({member, memberPendingInviteText, onRemoveClick}) => {
   const {t} = useTranslation();
   const [removalInProgress, setRemovalInProgress] = React.useState(false);
   const theme = useTscTheme();

   const onRemove = async () => {
      setRemovalInProgress(true);
      await onRemoveClick();
      setRemovalInProgress(false);
   };

   useEffect(() => {
      setRemovalInProgress(member.Role === null);
   }, [member]);

   return (
      <Wrapper>
         <GridMemberRow
            data-test-id={`team-member-${member.EmailAddress}`}
            changesDisabled={!onRemoveClick}
            key={member.EmailAddress}
         >
            <Avatar
               testId="team-member-avatar"
               avatarImageUrl={member.profileImageUrl}
               userDisplayName={member.DisplayName}
               userAltText={member.DisplayName}
               userID={member.TechSmithId}
            />
            <FlexContainer>
               <PrimaryText
                  data-test-id="team-member-display-name"
                  title={member.DisplayName}
               >
                  {member.DisplayName}
               </PrimaryText>
            </FlexContainer>

            <FlexContainer>
               {member.Role ? (
                  <RoleLabel
                     data-test-id="team-member-role-label"
                     labelText={
                        member.Role === GroupRoleTypes.notMember
                           ? memberPendingInviteText
                           : member.Role
                     }
                  />
               ) : (
                  <RoleLabel
                     data-test-id="team-member-invite-status-label"
                     labelText={memberPendingInviteText}
                  />
               )}
            </FlexContainer>

            {removalInProgress ? (
               <MemberSpinner
                  themeName={theme}
                  testId="team-member-loading-spinner"
               />
            ) : (
               onRemoveClick && (
                  <RemoveButton
                     buttonType={TscButtonType.tertiary}
                     title={t('general.remove')}
                     onClick={onRemove}
                     themeName={theme}
                     glyph={<Glyphs.CircleRemove16x16 />}
                     testId="team-member-remove-button"
                  />
               )
            )}
         </GridMemberRow>
      </Wrapper>
   );
};

export interface ITeamMemberListItemProps {
   member: IMemberDetailsWithProfileImageModel;
   memberPendingInviteText?: string;
   onRemoveClick?(): Promise<void>;
}

export default memo(TeamMemberListItemBase);
